import type { Heat, Stage } from '../../../shared/enums';
import { STAGES } from '../logic/leadStages';
import type { Lead } from '../../../shared/types';
import { ISO_DATE_PATTERN } from '../../../shared/validation';
import { addDays, formatDate, formatTime, relativeDay, todayLocal } from '../logic/dates';
import { h, span } from './dom';
import type { Child } from './dom';
import { HEAT_LABELS, STAGE_LABELS } from './labels';

// ---------------------------------------------------------------- buttons

export interface ButtonOpts {
  variant?: 'primary' | 'ghost' | 'danger' | 'subtle';
  size?: 'md' | 'lg' | 'sm';
  type?: 'button' | 'submit';
  disabled?: boolean;
  onClick?: (ev: MouseEvent) => void;
  class?: string;
  ariaLabel?: string;
}

export const btn = (label: Child, opts: ButtonOpts = {}): HTMLButtonElement =>
  h(
    'button',
    {
      class: ['btn', `btn--${opts.variant ?? 'ghost'}`, `btn--${opts.size ?? 'md'}`, opts.class]
        .filter(Boolean)
        .join(' '),
      type: opts.type ?? 'button',
      disabled: opts.disabled ?? false,
      'aria-label': opts.ariaLabel,
      onClick: opts.onClick,
    },
    label,
  );

export const linkBtn = (
  label: Child,
  href: string,
  opts: Omit<ButtonOpts, 'onClick' | 'type'> = {},
): HTMLAnchorElement =>
  h(
    'a',
    {
      class: ['btn', `btn--${opts.variant ?? 'ghost'}`, `btn--${opts.size ?? 'md'}`, opts.class]
        .filter(Boolean)
        .join(' '),
      href,
    },
    label,
  );

// ---------------------------------------------------------------- choice controls

export interface Option<T extends string> {
  value: T;
  label: string;
  hint?: string;
}

export interface SegmentedOpts<T extends string> {
  options: readonly Option<T>[];
  value: T | null;
  onChange: (value: T) => void;
  columns?: number;
  size?: 'lg' | 'md';
  name?: string;
}

/** Big single-choice buttons (radio semantics). */
export const segmented = <T extends string>(opts: SegmentedOpts<T>): HTMLDivElement => {
  const cols = opts.columns ?? Math.min(opts.options.length, 3);
  const group = h('div', {
    class: `seg seg--${opts.size ?? 'lg'}`,
    role: 'radiogroup',
    style: { gridTemplateColumns: `repeat(${cols}, 1fr)` },
  });
  const buttons: HTMLButtonElement[] = [];
  for (const o of opts.options) {
    const b = h(
      'button',
      {
        type: 'button',
        class: 'seg__btn',
        role: 'radio',
        'aria-checked': String(o.value === opts.value),
        dataset: { value: o.value },
        onClick: () => {
          for (const x of buttons) x.setAttribute('aria-checked', String(x.dataset.value === o.value));
          opts.onChange(o.value);
        },
      },
      span('seg__label', o.label),
      o.hint ? span('seg__hint', o.hint) : null,
    );
    buttons.push(b);
    group.appendChild(b);
  }
  return group;
};

export interface ChipsOpts<T extends string> {
  options: readonly Option<T>[];
  selected: ReadonlySet<T>;
  onToggle: (value: T, selected: boolean) => void;
}

/** Multi-select chips. */
export const chips = <T extends string>(opts: ChipsOpts<T>): HTMLDivElement =>
  h(
    'div',
    { class: 'chips', role: 'group' },
    opts.options.map((o) =>
      h(
        'button',
        {
          type: 'button',
          class: 'chip',
          'aria-pressed': String(opts.selected.has(o.value)),
          onClick: (ev: MouseEvent) => {
            const el = ev.currentTarget as HTMLButtonElement;
            const next = el.getAttribute('aria-pressed') !== 'true';
            el.setAttribute('aria-pressed', String(next));
            opts.onToggle(o.value, next);
          },
        },
        o.label,
      ),
    ),
  );

// ---------------------------------------------------------------- form fields

let fieldSeq = 0;
const LABELABLE = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** Label + control. Native inputs get a real <label>; composite controls (segmented, chips) get
 *  role=group + aria-labelledby so each option keeps its own accessible name. */
export const field = (
  label: string,
  control: HTMLElement,
  opts: { hint?: string; class?: string } = {},
): HTMLElement => {
  const cls = ['field', opts.class].filter(Boolean).join(' ');
  const hint = opts.hint ? span('field__hint', opts.hint) : null;
  if (LABELABLE.has(control.tagName)) {
    return h('label', { class: cls }, span('field__label', label), control, hint);
  }
  const id = `field-${++fieldSeq}`;
  const labelEl = h('span', { class: 'field__label', id }, label);
  control.setAttribute('aria-labelledby', id);
  return h('div', { class: cls, role: 'group', 'aria-labelledby': id }, labelEl, control, hint);
};

export interface InputOpts {
  value?: string | null;
  placeholder?: string;
  type?: string;
  inputmode?: string;
  autocapitalize?: string;
  autocomplete?: string;
  enterkeyhint?: string;
  required?: boolean;
  onInput?: (value: string, el: HTMLInputElement) => void;
  class?: string;
  name?: string;
  step?: string;
  min?: string;
  maxlength?: number;
  ariaLabel?: string;
  /** Names the field for a form that has to put the caret back on it (`[data-field="…"]`). */
  dataField?: string;
}

export const input = (opts: InputOpts = {}): HTMLInputElement =>
  h('input', {
    class: ['input', opts.class].filter(Boolean).join(' '),
    'aria-label': opts.ariaLabel,
    dataset: opts.dataField ? { field: opts.dataField } : undefined,
    type: opts.type ?? 'text',
    value: opts.value ?? '',
    placeholder: opts.placeholder,
    inputmode: opts.inputmode,
    autocapitalize: opts.autocapitalize,
    autocomplete: opts.autocomplete ?? 'off',
    enterkeyhint: opts.enterkeyhint,
    required: opts.required,
    name: opts.name,
    step: opts.step,
    min: opts.min,
    maxlength: opts.maxlength ? String(opts.maxlength) : undefined,
    onInput: (ev: Event) => {
      const el = ev.currentTarget as HTMLInputElement;
      opts.onInput?.(el.value, el);
    },
  });

export const textarea = (opts: {
  value?: string | null;
  placeholder?: string;
  rows?: number;
  onInput?: (v: string) => void;
  class?: string;
  maxlength?: number;
  ariaLabel?: string;
  dataField?: string;
}) =>
  h(
    'textarea',
    {
      class: ['input', 'textarea', opts.class].filter(Boolean).join(' '),
      'aria-label': opts.ariaLabel,
      dataset: opts.dataField ? { field: opts.dataField } : undefined,
      rows: String(opts.rows ?? 4),
      placeholder: opts.placeholder,
      maxlength: opts.maxlength ? String(opts.maxlength) : undefined,
      autocapitalize: 'sentences',
      onInput: (ev: Event) => opts.onInput?.((ev.currentTarget as HTMLTextAreaElement).value),
    },
    opts.value ?? '',
  );

export interface SelectOpts<T extends string> {
  options: readonly Option<T>[];
  value: T | null;
  onChange: (value: T) => void;
  placeholder?: string;
  class?: string;
  ariaLabel?: string;
}

export const select = <T extends string>(opts: SelectOpts<T>): HTMLSelectElement => {
  const el = h('select', {
    class: ['input', 'select', opts.class].filter(Boolean).join(' '),
    'aria-label': opts.ariaLabel,
    onChange: (ev: Event) => opts.onChange((ev.currentTarget as HTMLSelectElement).value as T),
  });
  if (opts.placeholder)
    el.appendChild(
      h('option', { value: '', disabled: true, selected: opts.value === null }, opts.placeholder),
    );
  for (const o of opts.options)
    el.appendChild(h('option', { value: o.value, selected: o.value === opts.value }, o.label));
  return el;
};

// ---------------------------------------------------------------- domain widgets

export const heatDot = (heat: Heat): HTMLSpanElement =>
  h('span', { class: `dot dot--${heat}`, title: HEAT_LABELS[heat], 'aria-label': HEAT_LABELS[heat] });

export const stageTag = (stage: Stage): HTMLSpanElement => span(`tag tag--${stage}`, STAGE_LABELS[stage]);

const STAGE_OPTIONS: Option<Stage>[] = STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] }));

/** One-tap stage changer (native select → wheel on iOS, sheet on Android). */
export const stageSelect = (value: Stage, onChange: (s: Stage) => void, cls = ''): HTMLSelectElement =>
  select<Stage>({
    options: STAGE_OPTIONS,
    value,
    onChange,
    class: `stagesel stagesel--${value} ${cls}`.trim(),
    ariaLabel: 'Stage',
  });

export interface RowOpts {
  title: Child;
  meta?: Child;
  leading?: Child;
  trailing?: Child;
  href?: string;
  onClick?: () => void;
  tone?: 'danger' | 'warn' | 'accent' | 'none';
  class?: string;
}

/** List row: tappable body (link) + optional non-tappable trailing control. */
export const row = (opts: RowOpts): HTMLDivElement => {
  const tag = opts.href ? 'a' : opts.onClick ? 'button' : 'div';
  const body = h(
    tag,
    {
      class: 'row__body',
      href: opts.href,
      type: tag === 'button' ? 'button' : undefined,
      onClick: opts.onClick,
    },
    opts.leading ? h('div', { class: 'row__leading' }, opts.leading) : null,
    h(
      'div',
      { class: 'row__text' },
      h('div', { class: 'row__title' }, opts.title),
      opts.meta ? h('div', { class: 'row__meta' }, opts.meta) : null,
    ),
  );
  return h(
    'div',
    {
      class: ['row', opts.tone && opts.tone !== 'none' ? `row--${opts.tone}` : '', opts.class]
        .filter(Boolean)
        .join(' '),
    },
    body,
    opts.trailing ? h('div', { class: 'row__trailing' }, opts.trailing) : null,
  );
};

export const leadTitle = (lead: Lead): Child => [heatDot(lead.heat), span('row__name', lead.business_name)];

export const section = (
  opts: { title: string; count?: number; tone?: 'danger' | 'warn' | 'accent'; action?: Child },
  ...children: Child[]
) =>
  h(
    'section',
    { class: ['section', opts.tone ? `section--${opts.tone}` : ''].filter(Boolean).join(' ') },
    h(
      'header',
      { class: 'section__head' },
      h(
        'h2',
        { class: 'section__title' },
        opts.title,
        opts.count !== undefined ? span('section__count mono', String(opts.count)) : null,
      ),
      opts.action ? h('div', { class: 'section__action' }, opts.action) : null,
    ),
    ...children,
  );

export const empty = (text: string, hint?: string): HTMLDivElement =>
  h(
    'div',
    { class: 'empty' },
    h('p', { class: 'empty__text' }, text),
    hint ? h('p', { class: 'empty__hint' }, hint) : null,
  );

/** Quick picks for the next action: relative chips + VISIBLE native date & time inputs.
 *  (The old hidden-input-behind-a-label trick never opened the picker on iOS — QA finding.) */
export const dateQuickPicks = (opts: {
  value: string | null;
  onChange: (v: string | null) => void;
  onTimeChange?: (v: string | null) => void;
  today?: string;
}): HTMLDivElement => {
  const today = opts.today ?? todayLocal();
  let value = opts.value;
  let time: string | null = null;
  const picks: { label: string; date: string }[] = [
    { label: 'Tomorrow', date: addDays(today, 1) },
    { label: '+3 days', date: addDays(today, 3) },
    { label: '+1 week', date: addDays(today, 7) },
    { label: '+2 weeks', date: addDays(today, 14) },
  ];
  const label = h('span', { class: 'datepick__value mono' });
  const native = h('input', {
    type: 'date',
    class: 'input chipinput',
    'aria-label': 'Date',
    value: value ?? '',
    min: today,
    max: '9999-12-31',
    // A value the API rejects (5-digit year from a desktop picker) counts as no date.
    onChange: (ev: Event) => {
      const v = (ev.currentTarget as HTMLInputElement).value;
      set(ISO_DATE_PATTERN.test(v) ? v : null);
    },
  });
  const timeInput = h('input', {
    type: 'time',
    class: 'input chipinput chipinput--time',
    'aria-label': 'Time (optional)',
    disabled: true,
    onChange: (ev: Event) => {
      time = (ev.currentTarget as HTMLInputElement).value || null;
      opts.onTimeChange?.(time);
      paintLabel();
    },
  });
  const buttons: HTMLButtonElement[] = [];
  const paintLabel = () => {
    label.textContent = value
      ? `${relativeDay(value, today)} · ${formatDate(value, today)}${time ? ` · ${formatTime(time)}` : ''}`
      : 'No date';
  };
  const set = (v: string | null) => {
    value = v;
    native.value = v ?? '';
    timeInput.disabled = !v;
    if (!v) {
      time = null;
      timeInput.value = '';
      opts.onTimeChange?.(null);
    }
    for (const b of buttons) b.setAttribute('aria-pressed', String(b.dataset.date === v));
    clearBtn.hidden = !v;
    paintLabel();
    opts.onChange(v);
  };
  for (const p of picks) {
    const b = h(
      'button',
      {
        type: 'button',
        class: 'chip',
        dataset: { date: p.date },
        'aria-pressed': String(p.date === value),
        onClick: () => set(p.date),
      },
      p.label,
    );
    buttons.push(b);
  }
  const clearBtn = h(
    'button',
    { type: 'button', class: 'chip chip--clear', hidden: !value, onClick: () => set(null) },
    'Clear',
  );
  const wrap = h(
    'div',
    { class: 'datepick' },
    h('div', { class: 'chips' }, ...buttons, clearBtn),
    h('div', { class: 'datepick__natives' }, native, timeInput),
    label,
  );
  paintLabel();
  return wrap;
};

/** Confirm dialog (native for reliability on mobile). */
export const confirmDialog = (message: string): boolean => window.confirm(message);
